import { c as _c } from "react/compiler-runtime";
import { AnyTranslationsFn } from "@/types/locale";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconAsterisk, IconRestore } from "@tabler/icons-react";
import { ZodObject } from "zod/v4";
import React, { useCallback, useEffect } from "react";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { useTranslations } from "next-intl";
import { EditButton } from "@components/common/buttons/edit-button";
import { AlertDialogWrapper } from "@components/common/containers/alert-dialog-wrapper";
import { FormFieldAbstract } from "@components/common/forms/form-field-abstract";
import { FormTextField } from "@components/common/forms/form-text-field";
import { ButtonWithTooltip } from "@components/ui/custom/button-with-tooltip";
import { Form } from "@components/ui/form";
import { Switch } from "@components/ui/switch";
import { zt } from "@lib/forms/zt";
import { useAnyTranslations } from "@lib/i18n/utils";
import { APP_NAME_DOMAIN } from "@features/application/app-consts";
import { ActivityMemberEntity, ActivityMemberInputView, ContributorFormOutputView } from "@features/proposals/types/member";
interface MemberPublicationConsentEditDialogProps {
  member: ActivityMemberEntity;
  isMe: boolean;
  consentForm: ContributorFormOutputView | null;
  patchMember: (member: ActivityMemberEntity, body: Partial<ActivityMemberInputView>) => Promise<undefined | ActivityMemberEntity>;
}

/**
 * Validation schema for a publication consent form.
 * For regular users (isMe=true): only allows editing consent acceptance.
 * For admins/coordinators (isMe=false, canPatch=true): allows setting third-party consent.
 */
const getValidationSchema = (t: AnyTranslationsFn, isMe: boolean): ZodObject => {
  if (isMe) {
    // User editing their own consent - only accepted field
    return zt.object({
      eventProfile: zt.object({
        consent: zt.object({
          content_publishing: zt.object({
            accepted: zt.boolean(t, true)
          })
        })
      })
    });
  }

  // Admin/coordinator editing - includes third-party fields
  return zt.object({
    eventProfile: zt.object({
      consent: zt.object({
        content_publishing: zt.object({
          accepted: zt.boolean(t, true),
          acceptedByThirdPartyReason: zt.string(t, true, 10, 500)
        })
      })
    })
  });
};

/**
 * Edit the dialog component for member publication consent.
 * Allows editing consent status with role-based access control.
 */
export const MemberPublicationConsentEditDialog = t0 => {
  const $ = _c(95);
  const {
    member,
    isMe,
    consentForm,
    patchMember
  } = t0;
  const tFormsErrors = useAnyTranslations("common.forms.errors");
  const t = useTranslations("features.proposals.ui.memberPublicationConsentStatus");
  const tCommon = useTranslations("common");
  const currentAcceptedState = consentForm?.accepted ?? false;
  let getInitialFormData;
  let t1;
  let t2;
  if ($[0] !== consentForm || $[1] !== isMe || $[2] !== tFormsErrors) {
    const schema = getValidationSchema(tFormsErrors, isMe);
    let t3;
    if ($[6] !== consentForm?.accepted || $[7] !== consentForm?.acceptedByThirdPartyReason) {
      t3 = () => ({
        eventProfile: {
          consent: {
            content_publishing: {
              accepted: consentForm?.accepted ?? false,
              acceptedByThirdPartyReason: consentForm?.acceptedByThirdPartyReason ?? ""
            }
          }
        }
      });
      $[6] = consentForm?.accepted;
      $[7] = consentForm?.acceptedByThirdPartyReason;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    consentForm?.accepted;
    consentForm?.acceptedByThirdPartyReason;
    getInitialFormData = t3;
    t2 = useForm;
    t1 = zodResolver(schema);
    $[0] = consentForm;
    $[1] = isMe;
    $[2] = tFormsErrors;
    $[3] = getInitialFormData;
    $[4] = t1;
    $[5] = t2;
  } else {
    getInitialFormData = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  let t3;
  if ($[9] !== getInitialFormData) {
    t3 = getInitialFormData();
    $[9] = getInitialFormData;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t1 || $[12] !== t3) {
    t4 = {
      resolver: t1,
      mode: "all",
      defaultValues: t3
    };
    $[11] = t1;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const form = t2(t4);
  const {
    isDirty,
    isValid
  } = form.formState;
  let t5;
  if ($[14] !== currentAcceptedState || $[15] !== form.control) {
    t5 = {
      control: form.control,
      name: "eventProfile.consent.content_publishing.accepted" as string,
      defaultValue: currentAcceptedState
    };
    $[14] = currentAcceptedState;
    $[15] = form.control;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const watchedAccepted = useWatch(t5);
  let t6;
  if ($[17] !== form || $[18] !== getInitialFormData || $[19] !== isDirty || $[20] !== isMe || $[21] !== member || $[22] !== patchMember) {
    t6 = () => form.handleSubmit(data => {
      if (!isDirty) {
        return Promise.reject();
      }
      const toSend = {
        eventProfile: {
          consent: {
            content_publishing: {
              accepted: data.eventProfile?.consent?.content_publishing?.accepted,
              ...(!isMe && data.eventProfile?.consent?.content_publishing?.acceptedByThirdPartyReason && {
                acceptedByThirdPartyReason: data.eventProfile.consent.content_publishing.acceptedByThirdPartyReason
              })
            }
          }
        }
      };
      return patchMember(member, toSend).then(updatedEntity => {
        if (updatedEntity) {
          form.reset(getInitialFormData());
        }
      }).catch(_temp);
    })();
    $[17] = form;
    $[18] = getInitialFormData;
    $[19] = isDirty;
    $[20] = isMe;
    $[21] = member;
    $[22] = patchMember;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const onSubmitHandler = t6;
  let t7;
  if ($[24] !== form || $[25] !== getInitialFormData) {
    t7 = () => form.reset(getInitialFormData());
    $[24] = form;
    $[25] = getInitialFormData;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const onCloseHandler = t7;
  let t8;
  if ($[27] !== form || $[28] !== getInitialFormData || $[29] !== isDirty) {
    t8 = () => {
      if (form && !isDirty) {
        form.reset(getInitialFormData());
      }
    };
    $[27] = form;
    $[28] = getInitialFormData;
    $[29] = isDirty;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== consentForm || $[32] !== form || $[33] !== getInitialFormData || $[34] !== isDirty) {
    t9 = [form, getInitialFormData, isDirty, consentForm];
    $[31] = consentForm;
    $[32] = form;
    $[33] = getInitialFormData;
    $[34] = isDirty;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  useEffect(t8, t9);
  let t10;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <EditButton />;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[37] !== isMe || $[38] !== t) {
    t11 = isMe ? t("editTitle.self") : t("editTitle.other");
    $[37] = isMe;
    $[38] = t;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== tCommon) {
    t12 = tCommon("words.verbs.save");
    $[40] = tCommon;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  const t13 = !isDirty || !isValid;
  let t14;
  if ($[42] !== onCloseHandler || $[43] !== onSubmitHandler) {
    t14 = {
      onAction: onSubmitHandler,
      onClose: onCloseHandler
    };
    $[42] = onCloseHandler;
    $[43] = onSubmitHandler;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== isMe || $[46] !== t) {
    t15 = isMe ? t("editDescription.self") : t("editDescription.other");
    $[45] = isMe;
    $[46] = t;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== tCommon) {
    t16 = tCommon("words.verbs.reset");
    $[48] = tCommon;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== form || $[51] !== getInitialFormData) {
    t17 = () => form.reset(getInitialFormData());
    $[50] = form;
    $[51] = getInitialFormData;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  const t18 = !isDirty;
  let t19;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <IconRestore />;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== t16 || $[55] !== t17 || $[56] !== t18) {
    t20 = <div className="flex items-center justify-end gap-2 border-b py-1"><ButtonWithTooltip tooltipContent={t16} size="icon" variant="ghost" onClick={t17} disabled={t18}>{t19}</ButtonWithTooltip></div>;
    $[54] = t16;
    $[55] = t17;
    $[56] = t18;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  let t21;
  if ($[58] !== isMe || $[59] !== t || $[60] !== watchedAccepted) {
    t21 = watchedAccepted ? isMe ? t("form.accepted.label.self.give") : t("form.accepted.label.other.give") : isMe ? t("form.accepted.label.self.revoke") : t("form.accepted.label.other.revoke");
    $[58] = isMe;
    $[59] = t;
    $[60] = watchedAccepted;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  let t22;
  if ($[62] !== isMe || $[63] !== t || $[64] !== watchedAccepted) {
    t22 = watchedAccepted ? isMe ? t("form.accepted.description.self.give") : t("form.accepted.description.other.give") : isMe ? t("form.accepted.description.self.revoke") : t("form.accepted.description.other.revoke");
    $[62] = isMe;
    $[63] = t;
    $[64] = watchedAccepted;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  let t23;
  if ($[66] !== member.rights.canPatch || $[67] !== t21 || $[68] !== t22) {
    t23 = <FormFieldAbstract name="eventProfile.consent.content_publishing.accepted" rights={member.rights.canPatch} className="max-w-sm" label={t21} description={t22} renderControl={_temp3} />;
    $[66] = member.rights.canPatch;
    $[67] = t21;
    $[68] = t22;
    $[69] = t23;
  } else {
    t23 = $[69];
  }
  let t24;
  if ($[70] !== isMe || $[71] !== member.rights.canPatch || $[72] !== t || $[73] !== watchedAccepted) {
    t24 = !isMe && <FormTextField name="eventProfile.consent.content_publishing.acceptedByThirdPartyReason" rights={member.rights.canPatch} label={watchedAccepted ? t("form.thirdPartyReason.label.give") : t("form.thirdPartyReason.label.revoke")} description={watchedAccepted ? t("form.thirdPartyReason.description.give") : t("form.thirdPartyReason.description.revoke")} required={true} textarea={true} />;
    $[70] = isMe;
    $[71] = member.rights.canPatch;
    $[72] = t;
    $[73] = watchedAccepted;
    $[74] = t24;
  } else {
    t24 = $[74];
  }
  let t25;
  if ($[75] !== t23 || $[76] !== t24) {
    t25 = <form className="space-y-4" onSubmit={_temp2}>{t23}{t24}</form>;
    $[75] = t23;
    $[76] = t24;
    $[77] = t25;
  } else {
    t25 = $[77];
  }
  let t26;
  if ($[78] !== form || $[79] !== t25) {
    t26 = <Form {...form}>{t25}</Form>;
    $[78] = form;
    $[79] = t25;
    $[80] = t26;
  } else {
    t26 = $[80];
  }
  let t27;
  if ($[81] !== isMe || $[82] !== tCommon) {
    t27 = !isMe && <div className="flex items-center gap-2 text-sm text-muted-foreground"><div className="w-5 justify-items-center"><IconAsterisk className="h-3 w-3 text-destructive" /></div><span>{tCommon("forms.errors.required")}</span></div>;
    $[81] = isMe;
    $[82] = tCommon;
    $[83] = t27;
  } else {
    t27 = $[83];
  }
  let t28;
  if ($[84] !== t20 || $[85] !== t26 || $[86] !== t27) {
    t28 = <div className="flex flex-col gap-4">{t20}{t26}{t27}</div>;
    $[84] = t20;
    $[85] = t26;
    $[86] = t27;
    $[87] = t28;
  } else {
    t28 = $[87];
  }
  let t29;
  if ($[88] !== t11 || $[89] !== t12 || $[90] !== t13 || $[91] !== t14 || $[92] !== t15 || $[93] !== t28) {
    t29 = <AlertDialogWrapper trigger={t10} title={t11} actionTitle={t12} actionDisabled={t13} handlers={t14} description={t15}>{t28}</AlertDialogWrapper>;
    $[88] = t11;
    $[89] = t12;
    $[90] = t13;
    $[91] = t14;
    $[92] = t15;
    $[93] = t28;
    $[94] = t29;
  } else {
    t29 = $[94];
  }
  return t29;
};
function _temp(e) {
  console.error(`[${APP_NAME_DOMAIN}] ${e}`);
}
function _temp2(e_0) {
  return e_0.preventDefault();
}
function _temp3(field, isDirty_0) {
  return <Switch {...field} checked={field.value ?? false} onCheckedChange={field.onChange} className={isDirty_0 ? "bg-gray-200 dark:bg-gray-700" : ""} />;
}